/**
 * UniFi Site Manager rows (staff view of unifi_site_map) plus the mapping
 * actions exposed by migration 20260918000035:
 *   • remap_unifi_sites()          — property directory → site mapping
 *   • relink_property_directory()  — location name → property code
 * Manual ownership changes use a live administrator RPC with explicit
 * customer/location IDs and the reviewed provider row revision.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Json } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';
import { providerAssignmentError } from './useAuvikTenants';

export interface UnifiSiteRow {
  id: string;
  unifi_site_id: string;
  unifi_host_id: string | null;
  unifi_host_name: string | null;
  site_name: string | null;
  customer_id: string | null;
  location_id: string | null;
  wifi_client_count: number | null;
  wired_client_count: number | null;
  ssid_count: number | null;
  mapped_by_user_id: string | null;
  mapped_at: string | null;
  attributes: Json | null;
  updated_at: string;
  customer: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
}

const SITE_COLUMNS = `id, unifi_site_id, unifi_host_id, unifi_host_name, site_name, customer_id, location_id,
  wifi_client_count, wired_client_count, ssid_count, mapped_by_user_id, mapped_at, attributes, updated_at,
  customer:customers(id, name), location:locations(id, name)`;

export function useUnifiSites() {
  return useQuery({
    queryKey: ['unifi-sites'],
    queryFn: async (): Promise<UnifiSiteRow[]> => {
      const { data, error } = await supabase
        .from('unifi_site_map')
        .select(SITE_COLUMNS)
        .order('unifi_host_name', { ascending: true })
        .order('site_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as UnifiSiteRow[];
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export interface DiscoveredUnifiSite { id: string; hostId: string; name: string; ambiguous: boolean }
export function useDiscoveredUnifiSites(enabled: boolean) {
  return useQuery({
    queryKey: ['mission-control-unifi-discovery'], enabled,
    queryFn: async (): Promise<DiscoveredUnifiSite[]> => {
      const { data, error } = await supabase.rpc('get_mission_control_provider_catalog', { p_provider_kind: 'unifi' });
      if (error) throw providerAssignmentError(error);
      const raw = data as unknown as { provider_id: string; name: string; host_id: string | null }[];
      const unique = new Map<string, { id: string; hostId: string; name: string }>();
      for (const row of Array.isArray(raw) ? raw : []) {
        if (typeof row?.provider_id !== 'string' || !row.provider_id) continue;
        const id = row.provider_id;
        const hostId = typeof row.host_id === 'string' ? row.host_id : '';
        unique.set(JSON.stringify([id, hostId]), { id, hostId, name: typeof row.name === 'string' && row.name ? row.name : id });
      }
      const rows = [...unique.values()];
      return rows.map((row) => ({ ...row, ambiguous: !row.hostId || rows.filter((other) => other.id === row.id).length !== 1 }));
    }, staleTime: 60_000,
  });
}

export function useUnifiSitesForLocation(locationId: string | undefined) {
  return useQuery({
    queryKey: ['unifi-sites', 'location', locationId],
    enabled: Boolean(locationId),
    queryFn: async (): Promise<UnifiSiteRow[]> => {
      const { data, error } = await supabase
        .from('unifi_site_map')
        .select(SITE_COLUMNS)
        .eq('location_id', locationId!)
        .order('site_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as UnifiSiteRow[];
    },
    staleTime: 60 * 1000,
  });
}

function invalidateUnifi(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['unifi-sites'] });
  qc.invalidateQueries({ queryKey: ['integration-status'] });
  qc.invalidateQueries({ queryKey: ['admin-platform-metrics'] });
  qc.invalidateQueries({ queryKey: ['admin-customer-network'] });
}

export interface MapUnifiSiteParams {
  id: string;
  customer_id: string;
  location_id: string;
  expected_updated_at: string;
  reason: string;
}

export function useMapUnifiSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: MapUnifiSiteParams) => {
      const { error } = await supabase.rpc('assign_mission_control_provider_location', {
        p_provider_kind: 'unifi', p_provider_row_id: params.id,
        p_expected_updated_at: params.expected_updated_at,
        p_customer_id: params.customer_id, p_location_id: params.location_id,
        p_reason: params.reason,
      });
      if (error) throw providerAssignmentError(error);
    },
    onSuccess: () => invalidateUnifi(qc),
    onError: () => invalidateUnifi(qc),
  });
}

export function useRegisterDiscoveredUnifiSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { provider_id: string; customer_id: string; location_id: string; reason: string }) => {
      const { error } = await supabase.rpc('register_mission_control_provider_location', {
        p_provider_kind: 'unifi', p_provider_id: params.provider_id,
        p_customer_id: params.customer_id, p_location_id: params.location_id, p_reason: params.reason,
      });
      if (error) throw providerAssignmentError(error);
    },
    onSuccess: () => invalidateUnifi(qc),
    onError: () => invalidateUnifi(qc),
  });
}

/** Re-runs the directory-driven mapping. Resolves with the number of hosts processed. */
export function useRemapUnifiSites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('remap_unifi_sites');
      if (error) throw error;
      return typeof data === 'number' ? data : 0;
    },
    onSuccess: () => invalidateUnifi(qc),
  });
}

/** Links locations to property_directory rows by name. Resolves with the number linked. */
export function useRelinkPropertyDirectory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('relink_property_directory');
      if (error) throw error;
      return typeof data === 'number' ? data : 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-directory'] });
      qc.invalidateQueries({ queryKey: ['admin-locations'] });
      qc.invalidateQueries({ queryKey: ['admin-location'] });
      invalidateUnifi(qc);
    },
  });
}
